import { type TenantProfile, getSupabase } from "@/lib/supabase";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "./useAuth";

const EMPTY: TenantProfile = {
  user_id: "",
  full_name: null,
  phone: null,
  contact_email: null,
  emergency_contact_name: null,
  emergency_contact_phone: null,
};

export function useTenantProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<TenantProfile>({ ...EMPTY });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setProfile({ ...EMPTY });
      setLoading(false);
      return;
    }
    setLoading(true);
    const supabase = getSupabase();
    const { data, error: err } = await supabase
      .from("tenant_profiles")
      .select("*")
      .eq("user_id", user.id)
      .maybeSingle();
    if (err) setError(err.message);
    setProfile((data as TenantProfile | null) ?? { ...EMPTY, user_id: user.id });
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (input: Omit<TenantProfile, "user_id">) => {
      if (!user) return { error: "not signed in" };
      const supabase = getSupabase();
      const payload: TenantProfile = { ...input, user_id: user.id };
      const { error: err } = await supabase.from("tenant_profiles").upsert(payload);
      if (err) return { error: err.message };
      setProfile(payload);
      return { error: null };
    },
    [user],
  );

  return { profile, loading, error, save, refresh };
}
